// New space and Edit space (SSettings, Spaces: the dashed "New space" card), for the director and admins: the name,
// the client (empty: internal), what it is, its colour (the dot in the sidebar and on its page) and, for a new one,
// its lead. The same sheet on a phone. A new space opens at once, ready for its first track.
import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { can, shownSpaceColor, SPACE_COLOR_CHOICES, type SessionUser, type SpaceColor, type SpaceDetail } from '@dpw/shared';
import { api } from '../api';
import { useChange, useSpace, useUsers } from '../lib/queries';
import { usePhone } from '../lib/media';
import { ErrorNote, Loading } from './bits';
import { Sheet } from './Sheet';
import { useToast } from './toast';
import { PSheet } from '../phone/kit';

/** The director and admins create spaces (the server checks the same). */
export const createsSpaces = (me: SessionUser) => me.isAdmin || can(me.role, 'space.all');
const COLOR_WORD: Record<(typeof SPACE_COLOR_CHOICES)[number], string> = { grn: 'Green', amb: 'Amber', red: 'Red' };

/** space: edit it; none: a new one. */
export function SpaceSheet({ me, space, onClose }: { me: SessionUser; space?: SpaceDetail; onClose: () => void }) {
  const phone = usePhone();
  const nav = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const users = useUsers();
  const [name, setName] = useState(space?.name ?? '');
  const [client, setClient] = useState(space?.clientName ?? '');
  const [desc, setDesc] = useState(space?.description ?? '');
  const [color, setColor] = useState<SpaceColor>(shownSpaceColor(space?.color));
  const [lead, setLead] = useState(me.id);
  const leads = (users.data ?? []).filter(u => u.isActive && can(u.role, 'activity.own'));
  const save = useChange(() => space
    ? api.spaces.update(space.id, { name: name.trim(), clientName: client.trim() || null, description: desc.trim() || null, color })
    : api.spaces.create({ name: name.trim(), clientName: client.trim() || null, description: desc.trim() || null, color, leadUserId: lead }));
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    save.mutate(undefined, {
      onSuccess: s => {
        void qc.invalidateQueries();
        onClose();
        if (space) toast(`Saved ${s.name}.`);
        else { toast(`Created ${s.name}. Add its first track.`); nav(`/spaces/${s.id}`); }
      },
    });
  };
  const inp = phone ? 'ph-inp' : 'inp';
  const title = space ? `Edit ${space.name}` : 'New space';
  const form = (
    <form className="col gap12 spform" onSubmit={submit} aria-label={title}>
      <label className="fld">Name<input className={inp} value={name} onChange={e => setName(e.target.value)} placeholder="Name, e.g. client or programme" maxLength={120} required autoFocus /></label>
      <label className="fld">Client (optional)<input className={inp} value={client} onChange={e => setClient(e.target.value)} placeholder="Dallah Healthcare. Leave empty for internal work" maxLength={120} /></label>
      <label className="fld">What it is (optional)<textarea className={inp} rows={3} value={desc} onChange={e => setDesc(e.target.value)} placeholder="One or two lines on the engagement" maxLength={2000} /></label>
      <fieldset className="fld spcolors"><legend>Colour</legend>
        <div className="rowc" role="radiogroup" aria-label="Colour">
          {SPACE_COLOR_CHOICES.map(c => (
            <button key={c} type="button" role="radio" aria-checked={color === c} aria-label={COLOR_WORD[c]} title={COLOR_WORD[c]} className={`spcol${color === c ? ' on' : ''}`} onClick={() => setColor(c)}>
              <span className={`dt ${c}`} />
            </button>
          ))}
        </div>
      </fieldset>
      {!space && (
        <label className="fld">Lead
          <select className={inp} value={lead} onChange={e => setLead(e.target.value)}>
            <option value={me.id}>{me.name} (you)</option>
            {leads.filter(u => u.id !== me.id).map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </label>
      )}
      <span className="xs mut">{space ? 'The lead and the people in it: Members.' : 'Add people and tracks once it is created. The director and admins see every space.'}</span>
      {save.error ? <ErrorNote error={save.error} /> : null}
      <button className={phone ? 'ph-btf' : 'bt pri'} disabled={save.isPending || !name.trim()}>{save.isPending ? <span className="spin" /> : null}{space ? 'Save' : 'Create space'}</button>
    </form>
  );
  if (phone) return <PSheet label={title} onClose={onClose}><h2>{title}</h2>{form}</PSheet>;
  return (
    <Sheet crumbs={<><span>Settings</span><span>/</span><b>{title}</b></>} onClose={onClose} width={560} label={title}>
      <h2 className="stitle" style={{ fontSize: 24 }}>{title}</h2>
      {form}
    </Sheet>
  );
}

/** Edit a space by its id (the Settings list has only its summary): loads it, then the same sheet. */
export function EditSpaceSheet({ me, spaceId, onClose }: { me: SessionUser; spaceId: string; onClose: () => void }) {
  const q = useSpace(spaceId);
  const phone = usePhone();
  if (q.data) return <SpaceSheet me={me} space={q.data} onClose={onClose} />;
  const body = q.error ? <ErrorNote error={q.error} /> : <Loading />;
  return phone ? <PSheet label="Edit space" onClose={onClose}>{body}</PSheet>
    : <Sheet crumbs={<><span>Settings</span><span>/</span><b>Edit space</b></>} onClose={onClose} width={560} label="Edit space">{body}</Sheet>;
}
